import { Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router';
import { TooltipProvider } from '@bill-alps/ui';
import { PageLoader } from './components/page-loader';
import { lazyModule, lazyPage } from '@/lib/deploy-skew/lazy-page';
import * as Sentry from '@sentry/react';
import { AuthLayout } from './layouts/auth-layout';
import { AppLayout } from './layouts/app-layout';
import {
  ProtectedRoute,
  OnboardingRoute,
  PublicRoute,
  SandboxRoute,
} from './components/protected-route';
import { ModuleGate } from './components/module-gate';
import { AccountAdminGate } from './components/account-admin-gate';
import { FiduciaireGate } from './components/fiduciaire-gate';
import { OwnCompanyGate } from './components/own-company-gate';
import { ModuleWriteGate } from './components/module-write-gate';
import { CatalogGate } from './components/catalog-gate';
import { StatementReportingGate } from './components/statement-reporting-gate';
import { LedgerGate } from './components/ledger-gate';
import { LocaleSync } from './components/locale-sync';
import {
  INVOICE_IMPORT_AVAILABLE,
  IS_SIGN_UP_DISABLED,
} from '@/lib/runtime/feature-flags';
import { GlobalBanners } from './components/global-banners';
import { SentryUserSync } from './components/sentry-user-sync';
import { StaleBundleDialog } from './components/stale-bundle-dialog';
import { MOBILE_TITLE_HANDLES } from '@/lib/routing/mobile-route-title';
// Eager: the landing route (lazying it would only add a round-trip) and the
// 404, which must render even when a chunk fetch is what failed.
import { DashboardPage } from './features/dashboard/dashboard-page';
import { NotFoundPage } from './features/not-found/not-found-page';
import { PortalNotFoundPage } from './features/portal/portal-not-found-page';

// Every page below is code-split so a user of one module never downloads the
// others — the `<Suspense>` boundary lives in each layout, around its
// `<Outlet />`, and `lib/deploy-skew/lazy-page.ts` handles a chunk the last deploy removed.

// Auth — a signed-in user never needs these, and vice versa.
const SignUpPage = lazyPage(
  () => import('./features/auth/sign-up-page'),
  'SignUpPage',
);
const SignUpVerifyPage = lazyPage(
  () => import('./features/auth/sign-up-verify-page'),
  'SignUpVerifyPage',
);
const SignInPage = lazyPage(
  () => import('./features/auth/sign-in-page'),
  'SignInPage',
);
const SSOCallbackPage = lazyPage(
  () => import('./features/auth/sso-callback-page'),
  'SSOCallbackPage',
);
const OnboardingPage = lazyPage(
  () => import('./features/auth/onboarding-page'),
  'OnboardingPage',
);
const SubscribePage = lazyPage(
  () => import('./features/auth/subscribe-page'),
  'SubscribePage',
);
const JoinCompanyPage = lazyPage(
  () => import('./features/auth/join-company-page'),
  'JoinCompanyPage',
);
const OwnershipTransferPage = lazyPage(
  () => import('./features/auth/ownership-transfer-page'),
  'OwnershipTransferPage',
);
const AccessSuspendedPage = lazyPage(
  () => import('./features/auth/access-suspended-page'),
  'AccessSuspendedPage',
);

// Invoicing module
const BillsPage = lazyPage(
  () => import('./features/bills/bills-page'),
  'BillsPage',
);
const QuotesPage = lazyPage(
  () => import('./features/quotes/quotes-page'),
  'QuotesPage',
);
const RecurringInvoicesPage = lazyPage(
  () => import('./features/recurringInvoices/recurring-invoices-page'),
  'RecurringInvoicesPage',
);
const RecurringInvoiceEditPage = lazyPage(
  () => import('./features/recurringInvoices/recurring-invoice-edit-page'),
  'RecurringInvoiceEditPage',
);
const ClientsPage = lazyPage(
  () => import('./features/clients/clients-page'),
  'ClientsPage',
);
const ClientImportPage = lazyPage(
  () => import('./features/clients/import/client-import-page'),
  'ClientImportPage',
);
const InvoiceImportPage = lazyPage(
  () => import('./features/invoice-import/invoice-import-page'),
  'InvoiceImportPage',
);
const ProductsPage = lazyPage(
  () => import('./features/products/products-page'),
  'ProductsPage',
);
const ProductListsPage = lazyPage(
  () => import('./features/products/product-lists-page'),
  'ProductListsPage',
);
const TemplatesPage = lazyPage(
  () => import('./features/templates/templates-page'),
  'TemplatesPage',
);

// Expenses module
const SupplierBillsPage = lazyPage(
  () => import('./features/supplier-bills/supplier-bills-page'),
  'SupplierBillsPage',
);
const AnnualReviewPage = lazyPage(
  () => import('./features/supplier-bills/annual-review-page'),
  'AnnualReviewPage',
);
const DriveImportPage = lazyPage(
  () => import('./features/supplier-bills/import/drive-import-page'),
  'DriveImportPage',
);
// Financial-statements module
const FinancialStatementsPage = lazyPage(
  () => import('./features/financial-statements/financial-statements-page'),
  'FinancialStatementsPage',
);
const CsvImportPage = lazyPage(
  () => import('./features/financial-statements/import/csv-import-page'),
  'CsvImportPage',
);
const ReconciliationPage = lazyPage(
  () =>
    import('./features/financial-statements/reconciliation/reconciliation-page'),
  'ReconciliationPage',
);
const ChartOfAccountsPage = lazyPage(
  () => import('./features/ledger/chart-of-accounts-page'),
  'ChartOfAccountsPage',
);
const ActivationWizardPage = lazyPage(
  () => import('./features/ledger/activation-wizard-page'),
  'ActivationWizardPage',
);
const JournalPage = lazyPage(
  () => import('./features/ledger/journal-page'),
  'JournalPage',
);
const FiscalYearsPage = lazyPage(
  () => import('./features/ledger/fiscal-years-page'),
  'FiscalYearsPage',
);
const ClassificationPage = lazyPage(
  () => import('./features/ledger/classification-page'),
  'ClassificationPage',
);
const FixedAssetsPage = lazyPage(
  () => import('./features/fixed-assets/fixed-assets-page'),
  'FixedAssetsPage',
);
const ClosingWizardPage = lazyPage(
  () => import('./features/ledger/closing-wizard-page'),
  'ClosingWizardPage',
);
const EntryDetailPage = lazyPage(
  () => import('./features/ledger/entry-detail-page'),
  'EntryDetailPage',
);
const ManualEntryPage = lazyPage(
  () => import('./features/ledger/manual-entry-page'),
  'ManualEntryPage',
);
const VatReturnsPage = lazyPage(
  () => import('./features/vat/vat-returns-page'),
  'VatReturnsPage',
);
const AnnualAccountsPage = lazyPage(
  () => import('./features/annual-accounts/annual-accounts-page'),
  'AnnualAccountsPage',
);

// Payroll module
const EmployeesPage = lazyPage(
  () => import('./features/employees/employees-page'),
  'EmployeesPage',
);
const PayslipsPage = lazyPage(
  () => import('./features/payslips/payslips-page'),
  'PayslipsPage',
);
const EmployeePayslipsPage = lazyPage(
  () => import('./features/payslips/employee-payslips-page'),
  'EmployeePayslipsPage',
);
const EmployeeAbsencesPage = lazyPage(
  () => import('./features/absences/employee-absences-page'),
  'EmployeeAbsencesPage',
);
const EmployeeChangeRequestsPage = lazyPage(
  () => import('./features/employees/change-requests-page'),
  'EmployeeChangeRequestsPage',
);

// Employee portal — public magic-link routes, no Clerk involved
const PortalLayout = lazyPage(
  () => import('./features/portal/portal-layout'),
  'PortalLayout',
);
const PortalHomePage = lazyPage(
  () => import('./features/portal/portal-home-page'),
  'PortalHomePage',
);
const PortalPayslipsPage = lazyPage(
  () => import('./features/portal/portal-payslips-page'),
  'PortalPayslipsPage',
);
const PortalAbsencesPage = lazyPage(
  () => import('./features/portal/portal-absences-page'),
  'PortalAbsencesPage',
);
const PortalProfilePage = lazyPage(
  () => import('./features/portal/portal-profile-page'),
  'PortalProfilePage',
);
const PortalCertificatePage = lazyPage(
  () => import('./features/portal/portal-certificate-page'),
  'PortalCertificatePage',
);
const PortalRequestChangesPage = lazyPage(
  () => import('./features/portal/portal-request-changes-page'),
  'PortalRequestChangesPage',
);
const PortalRequestLinkPage = lazyPage(
  () => import('./features/portal/portal-request-link-page'),
  'PortalRequestLinkPage',
);

// Cross-module settings
const AccountPage = lazyPage(
  () => import('./features/account/account-page'),
  'AccountPage',
);
const CompanyPage = lazyPage(
  () => import('./features/company/company-page'),
  'CompanyPage',
);
const MandatesPage = lazyPage(
  () => import('./features/fiduciaire/mandates-page'),
  'MandatesPage',
);
const MandateRequestRespondPage = lazyPage(
  () => import('./features/fiduciaire/mandate-request-respond-page'),
  'MandateRequestRespondPage',
);
const SupportPage = lazyPage(
  () => import('./features/support/support-page'),
  'SupportPage',
);

const AdminDataPage = lazyModule(
  () => import('./features/admin/admin-data-page'),
);
const AdminStatsPage = lazyModule(
  () => import('./features/admin/admin-stats-page'),
);
const AdminCompliancePage = lazyModule(
  () => import('./features/admin/compliance/compliance-rules-page'),
);
const AdminComplianceInstrumentPage = lazyPage(
  () => import('./features/admin/compliance/instrument-pages'),
  'InstrumentPage',
);
const AdminComplianceNewInstrumentPage = lazyPage(
  () => import('./features/admin/compliance/instrument-pages'),
  'NewInstrumentPage',
);
const AdminComplianceRuleViewPage = lazyPage(
  () => import('./features/admin/compliance/rule-view-page'),
  'RuleViewPage',
);
const AdminComplianceRuleEditPage = lazyPage(
  () => import('./features/admin/compliance/rule-edit-page'),
  'RuleEditPage',
);

/**
 * Sandbox builds only. Same trick as `devRoutes()` below: the `lazyPage()`
 * call lives inside the function so Rollup can drop the whole chunk from a
 * normal build — hence also the literal `import.meta.env.VITE_APP_ENV` at the
 * call site rather than the `IS_SANDBOX` flag, which is what Vite inlines and
 * folds away (an imported const leaves the chunk in the bundle, unfetched).
 */
function sandboxRoutes() {
  const SandboxSetupPage = lazyPage(
    () => import('./features/sandbox/sandbox-setup-page'),
    'SandboxSetupPage',
  );

  return [
    {
      element: <SandboxRoute />,
      children: [
        {
          element: <AuthLayout />,
          children: [{ path: '/sandbox/setup', element: <SandboxSetupPage /> }],
        },
      ],
    },
  ];
}

function devProdImportRoutes() {
  const SandboxSetupPage = lazyPage(
    () => import('./features/sandbox/sandbox-setup-page'),
    'SandboxSetupPage',
  );

  return [
    {
      element: <SandboxRoute />,
      children: [
        {
          element: <AuthLayout />,
          children: [
            { path: '/dev/prod-import', element: <SandboxSetupPage /> },
          ],
        },
      ],
    },
  ];
}

/**
 * Dev-only drills, reachable by URL only (no nav entry). The `lazyPage()` call
 * and its dynamic import live *inside* this function on purpose: at module
 * scope Rollup cannot prove the call is side-effect free, so it would keep it
 * and ship a dead chunk to production. As an unused function declaration —
 * `import.meta.env.DEV` is inlined to `false` at build time, killing the only
 * call site — the whole thing, chunk included, drops out of the bundle.
 */
function devRoutes() {
  const StaleBundlePage = lazyPage(
    () => import('./features/dev/stale-bundle-page'),
    'StaleBundlePage',
  );
  const ErrorBoundaryPage = lazyPage(
    () => import('./features/dev/error-boundary-page'),
    'ErrorBoundaryPage',
  );
  const ModelComparisonPage = lazyPage(
    () => import('./features/dev/model-comparison-page'),
    'ModelComparisonPage',
  );

  return [
    { path: '/dev/stale-bundle', element: <StaleBundlePage /> },
    { path: '/dev/error-boundary', element: <ErrorBoundaryPage /> },
    { path: '/dev/model-comparison', element: <ModelComparisonPage /> },
  ];
}

/**
 * Developer documentation (the data-model ER diagram), reachable in local dev
 * *and* on sandbox builds so external developers can read it — unlike the
 * `devRoutes()` drills above, which stay local-only. Same chunk-dropping
 * contract as `sandboxRoutes()`: the `lazyPage()` call stays inside the
 * function and the call site tests literal `import.meta.env.*` expressions
 * (not the imported `IS_SANDBOX`), so a production build never ships the
 * mermaid chunk.
 */
function devDocsRoutes() {
  const DatabaseSchemaPage = lazyPage(
    () => import('./features/dev/database-schema-page'),
    'DatabaseSchemaPage',
  );

  return [{ path: '/dev/database', element: <DatabaseSchemaPage /> }];
}

function devPortalRoutes() {
  const ErrorBoundaryPage = lazyPage(
    () => import('./features/dev/error-boundary-page'),
    'ErrorBoundaryPage',
  );

  return [
    {
      path: '__dev/error-boundary',
      element: <ErrorBoundaryPage />,
    },
  ];
}

function devAuthRoutes() {
  const AuthLayoutDebugPage = lazyPage(
    () => import('./features/dev/auth-layout-debug-page'),
    'AuthLayoutDebugPage',
  );

  return [
    {
      element: <AuthLayout />,
      children: [
        { path: '/dev/auth-layout', element: <AuthLayoutDebugPage /> },
      ],
    },
  ];
}

/**
 * The portal routes hang directly under RootLayout, which has no Suspense
 * boundary of its own (the app/auth layouts provide theirs) — this wrapper
 * supplies it for the code-split portal chunks.
 */
function PortalRoutes() {
  return (
    <Suspense
      fallback={
        <div className="flex min-h-screen items-center justify-center">
          <PageLoader />
        </div>
      }
    >
      <Outlet />
    </Suspense>
  );
}

function RootLayout() {
  return (
    <>
      <LocaleSync />
      <SentryUserSync />
      {/* Above every layout: a stale bundle breaks the auth and onboarding
          screens too, and neither mounts a `<Toaster />`. */}
      <StaleBundleDialog />
      {/* Sticky bar stack — the sandbox "this is a copy of production" strip
          and any admin-authored maintenance notice. Above every shell so the
          auth screens, the setup page and the employee portal carry it too;
          the shells subtract the stack's height from their own. */}
      <GlobalBanners />
      {/* One provider for the whole app: every tooltip below shares its open
          delay, so moving between two adjacent triggers doesn't re-wait. */}
      <TooltipProvider>
        <Outlet />
      </TooltipProvider>
    </>
  );
}

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

export const router = sentryCreateBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      // Public auth routes - redirect to dashboard if already signed in
      {
        element: <PublicRoute />,
        children: [
          {
            element: <AuthLayout />,
            children: [
              { path: '/sign-in', element: <SignInPage /> },
              // No sign-up in a sandbox: the account has to exist in
              // production first — signing up here would create a Clerk user
              // no import can ever resolve.
              {
                path: '/sign-up',
                element: IS_SIGN_UP_DISABLED ? (
                  <Navigate to="/sign-in" replace />
                ) : (
                  <SignUpPage />
                ),
              },
              {
                path: '/sign-up/verify',
                element: IS_SIGN_UP_DISABLED ? (
                  <Navigate to="/sign-in" replace />
                ) : (
                  <SignUpVerifyPage />
                ),
              },
            ],
          },
        ],
      },
      // SSO callbacks - no guard needed, Clerk handles the redirect
      {
        element: <AuthLayout />,
        children: [
          { path: '/join-company', element: <JoinCompanyPage /> },
          {
            path: '/ownership-transfer',
            element: <OwnershipTransferPage />,
          },
          {
            path: '/sign-up/sso-callback',
            element: <SSOCallbackPage />,
          },
          {
            path: '/sign-in/sso-callback',
            element: <SSOCallbackPage />,
          },
        ],
      },
      ...(import.meta.env.VITE_APP_ENV === 'sandbox' ? sandboxRoutes() : []),
      ...(import.meta.env.DEV &&
      import.meta.env.VITE_APP_ENV === 'dev-prod-import'
        ? devProdImportRoutes()
        : []),
      ...(import.meta.env.DEV ? devAuthRoutes() : []),
      // Employee portal — public, token-in-URL capability auth. NOT behind
      // PublicRoute: a signed-in employer must be able to open a portal link.
      {
        element: <PortalRoutes />,
        children: [
          // Deliberately tombstone every former unscoped path. The matching
          // words are reserved and cannot become company slugs.
          { path: '/portal', element: <NotFoundPage /> },
          { path: '/portal/request', element: <NotFoundPage /> },
          { path: '/portal/payslips', element: <NotFoundPage /> },
          { path: '/portal/absences', element: <NotFoundPage /> },
          { path: '/portal/profile', element: <NotFoundPage /> },
          { path: '/portal/profile/request', element: <NotFoundPage /> },
          { path: '/portal/certificate', element: <NotFoundPage /> },
          {
            path: '/portal/:companySlug/request',
            element: <PortalRequestLinkPage />,
          },
          {
            element: <PortalLayout />,
            path: '/portal/:companySlug',
            children: [
              { index: true, element: <PortalHomePage /> },
              { path: 'payslips', element: <PortalPayslipsPage /> },
              { path: 'absences', element: <PortalAbsencesPage /> },
              { path: 'profile', element: <PortalProfilePage /> },
              {
                path: 'profile/request',
                element: <PortalRequestChangesPage />,
              },
              {
                path: 'certificate',
                element: <PortalCertificatePage />,
              },
              ...(import.meta.env.DEV ? devPortalRoutes() : []),
              { path: '*', element: <PortalNotFoundPage /> },
            ],
          },
        ],
      },
      // Onboarding - requires sign-in, redirects to dashboard if already onboarded
      {
        element: <OnboardingRoute />,
        children: [
          {
            element: <AuthLayout />,
            children: [
              { path: '/onboarding', element: <OnboardingPage /> },
              {
                path: '/access-suspended',
                element: <AccessSuspendedPage />,
              },
            ],
          },
        ],
      },
      // Protected app routes - requires sign-in + onboarding complete
      {
        element: <ProtectedRoute />,
        children: [
          // Post-onboarding upsell — centered auth-style layout, no app nav
          {
            element: <AuthLayout />,
            children: [
              { path: '/welcome/subscribe', element: <SubscribePage /> },
            ],
          },
          {
            element: <AppLayout />,
            children: [
              {
                path: '/dashboard',
                element: <DashboardPage />,
                handle: MOBILE_TITLE_HANDLES.dashboard,
              },
              {
                element: <ModuleGate module="invoicing" />,
                children: [
                  {
                    element: <ModuleWriteGate module="invoicing" />,
                    children: [
                      {
                        path: '/quotes/:quoteNumber/edit',
                        element: <QuotesPage />,
                        handle: MOBILE_TITLE_HANDLES.quoteEdit,
                      },
                      {
                        path: '/bills/:invoiceNumber/edit',
                        element: <BillsPage />,
                        handle: MOBILE_TITLE_HANDLES.billEdit,
                      },
                      {
                        path: '/recurring-invoices/new',
                        element: <RecurringInvoiceEditPage mode="create" />,
                        handle: MOBILE_TITLE_HANDLES.recurringInvoiceCreate,
                      },
                      {
                        path: '/recurring-invoices/:id/edit',
                        element: <RecurringInvoiceEditPage mode="edit" />,
                        handle: MOBILE_TITLE_HANDLES.recurringInvoiceEdit,
                      },
                      {
                        path: '/clients/import',
                        element: <ClientImportPage />,
                        handle: MOBILE_TITLE_HANDLES.clientImport,
                      },
                      // Static, so it wins over `/bills/:invoiceNumber`.
                      ...(INVOICE_IMPORT_AVAILABLE
                        ? [
                            {
                              path: '/bills/import',
                              element: <InvoiceImportPage />,
                              handle: MOBILE_TITLE_HANDLES.billImport,
                            },
                          ]
                        : []),
                      {
                        path: '/templates',
                        element: <TemplatesPage />,
                        handle: MOBILE_TITLE_HANDLES.templates,
                      },
                    ],
                  },
                  {
                    path: '/quotes/:quoteNumber',
                    element: <QuotesPage />,
                    handle: MOBILE_TITLE_HANDLES.quotes,
                  },
                  {
                    path: '/quotes',
                    element: <QuotesPage />,
                    handle: MOBILE_TITLE_HANDLES.quotes,
                  },
                  {
                    path: '/bills/:invoiceNumber',
                    element: <BillsPage />,
                    handle: MOBILE_TITLE_HANDLES.bills,
                  },
                  {
                    path: '/bills',
                    element: <BillsPage />,
                    handle: MOBILE_TITLE_HANDLES.bills,
                  },
                  {
                    path: '/recurring-invoices',
                    element: <RecurringInvoicesPage />,
                    handle: MOBILE_TITLE_HANDLES.recurringInvoices,
                  },
                  {
                    path: '/clients/:slug',
                    element: <ClientsPage />,
                    handle: MOBILE_TITLE_HANDLES.clients,
                  },
                  {
                    path: '/clients',
                    element: <ClientsPage />,
                    handle: MOBILE_TITLE_HANDLES.clients,
                  },
                  {
                    element: <CatalogGate />,
                    children: [
                      {
                        path: '/products/:slug',
                        element: <ProductsPage />,
                        handle: MOBILE_TITLE_HANDLES.products,
                      },
                      {
                        path: '/products',
                        element: <ProductsPage />,
                        handle: MOBILE_TITLE_HANDLES.products,
                      },
                      {
                        path: '/product-lists/:slug',
                        element: <ProductListsPage />,
                        handle: MOBILE_TITLE_HANDLES.productLists,
                      },
                      {
                        path: '/product-lists',
                        element: <ProductListsPage />,
                        handle: MOBILE_TITLE_HANDLES.productLists,
                      },
                    ],
                  },
                ],
              },
              {
                element: <ModuleGate module="accounting" />,
                children: [
                  {
                    path: '/expenses',
                    element: <SupplierBillsPage />,
                    handle: MOBILE_TITLE_HANDLES.expenses,
                  },
                  {
                    element: <ModuleWriteGate module="accounting" />,
                    children: [
                      {
                        path: '/expenses/review',
                        element: <AnnualReviewPage />,
                        handle: MOBILE_TITLE_HANDLES.expenseReview,
                      },
                      {
                        path: '/expenses/import',
                        element: <DriveImportPage />,
                        handle: MOBILE_TITLE_HANDLES.expenseImport,
                      },
                    ],
                  },
                ],
              },
              {
                element: <ModuleGate module="accounting" />,
                children: [
                  // One-release URL shims (ledger GA, 2026-09-10). The nginx
                  // layer sends 301s in production; Navigate covers dev and
                  // deployments whose edge still serves the SPA directly.
                  {
                    path: '/financial-statements',
                    element: <Navigate to="/accounting/bank" replace />,
                  },
                  {
                    path: '/financial-statements/import',
                    element: <Navigate to="/accounting/bank/import" replace />,
                  },
                  {
                    path: '/financial-statements/reconcile',
                    element: (
                      <Navigate to="/accounting/bank/reconcile" replace />
                    ),
                  },
                  {
                    path: '/financial-statements/generate',
                    element: (
                      <Navigate to="/accounting/annual-accounts" replace />
                    ),
                  },
                  {
                    path: '/accounting/bank',
                    element: <FinancialStatementsPage />,
                    handle: MOBILE_TITLE_HANDLES.financialStatements,
                  },
                  // Read access is enough to see the accounts and their
                  // versions. The page shows the Business upsell and the
                  // ledger prerequisites itself; every backend function
                  // re-checks the plan.
                  {
                    element: <StatementReportingGate />,
                    children: [
                      {
                        path: '/accounting/annual-accounts/:fiscalYearId?',
                        element: <AnnualAccountsPage />,
                        handle: MOBILE_TITLE_HANDLES.annualAccounts,
                      },
                      {
                        path: '/accounting/statement',
                        element: (
                          <Navigate to="/accounting/annual-accounts" replace />
                        ),
                      },
                    ],
                  },
                  {
                    element: <ModuleWriteGate module="accounting" />,
                    children: [
                      {
                        path: '/accounting/bank/import',
                        element: <CsvImportPage />,
                        handle: MOBILE_TITLE_HANDLES.financialStatementImport,
                      },
                      {
                        path: '/accounting/bank/reconcile',
                        element: <ReconciliationPage />,
                        handle:
                          MOBILE_TITLE_HANDLES.financialStatementReconcile,
                      },
                    ],
                  },
                  {
                    element: <LedgerGate />,
                    children: [
                      {
                        path: '/accounting/to-classify',
                        element: <ClassificationPage />,
                      },
                      {
                        path: '/accounting/chart',
                        element: <ChartOfAccountsPage />,
                        handle: MOBILE_TITLE_HANDLES.chartOfAccounts,
                      },
                      {
                        path: '/accounting/journal',
                        element: <JournalPage />,
                        handle: MOBILE_TITLE_HANDLES.journal,
                      },
                      {
                        path: '/accounting/journal/:id',
                        element: <EntryDetailPage />,
                        handle: MOBILE_TITLE_HANDLES.journal,
                      },
                      {
                        path: '/accounting/vat',
                        element: <VatReturnsPage />,
                        handle: MOBILE_TITLE_HANDLES.vatReturns,
                      },
                      {
                        path: '/accounting/fiscal-years',
                        element: <FiscalYearsPage />,
                        handle: MOBILE_TITLE_HANDLES.fiscalYears,
                      },
                      {
                        path: '/accounting/fixed-assets',
                        element: <FixedAssetsPage />,
                        handle: MOBILE_TITLE_HANDLES.fixedAssets,
                      },

                      {
                        element: <ModuleWriteGate module="accounting" />,
                        children: [
                          {
                            path: '/accounting/activate',
                            element: <ActivationWizardPage />,
                            handle: MOBILE_TITLE_HANDLES.ledgerActivation,
                          },
                          {
                            path: '/accounting/journal/new',
                            element: <ManualEntryPage />,
                            handle: MOBILE_TITLE_HANDLES.manualEntry,
                          },
                          {
                            path: '/accounting/fiscal-years/:id/close',
                            element: <ClosingWizardPage />,
                            handle: MOBILE_TITLE_HANDLES.closing,
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                element: <ModuleGate module="payroll" />,
                children: [
                  {
                    path: '/employees/:slug/payslips',
                    element: <EmployeePayslipsPage />,
                    handle: MOBILE_TITLE_HANDLES.employeePayslips,
                  },
                  {
                    path: '/employees/:slug/absences',
                    element: <EmployeeAbsencesPage />,
                    handle: MOBILE_TITLE_HANDLES.employeeAbsences,
                  },
                  {
                    path: '/employees/requests',
                    element: <EmployeeChangeRequestsPage />,
                    handle: MOBILE_TITLE_HANDLES.employeeChangeRequests,
                  },
                  {
                    path: '/employees/:slug',
                    element: <EmployeesPage />,
                    handle: MOBILE_TITLE_HANDLES.employees,
                  },
                  {
                    path: '/employees',
                    element: <EmployeesPage />,
                    handle: MOBILE_TITLE_HANDLES.employees,
                  },
                  {
                    path: '/payslips/:id',
                    element: <PayslipsPage />,
                    handle: MOBILE_TITLE_HANDLES.payslips,
                  },
                  {
                    path: '/payslips',
                    element: <PayslipsPage />,
                    handle: MOBILE_TITLE_HANDLES.payslips,
                  },
                ],
              },
              {
                element: <AccountAdminGate />,
                children: [
                  {
                    path: '/company',
                    element: <CompanyPage />,
                    handle: MOBILE_TITLE_HANDLES.company,
                  },
                ],
              },
              {
                element: <FiduciaireGate />,
                children: [
                  {
                    path: '/mandates',
                    element: <MandatesPage />,
                    handle: MOBILE_TITLE_HANDLES.mandates,
                  },
                ],
              },
              {
                element: <OwnCompanyGate />,
                children: [
                  {
                    path: '/account',
                    element: <AccountPage />,
                    handle: MOBILE_TITLE_HANDLES.account,
                  },
                ],
              },
              // The email link of a mandate request. No gate: the responder
              // is an admin of the TARGET company, which may not be the
              // active one — the page checks the role itself.
              {
                path: '/mandate-requests/respond',
                element: <MandateRequestRespondPage />,
                handle: MOBILE_TITLE_HANDLES.mandateRequest,
              },
              {
                path: '/support',
                element: <SupportPage />,
                handle: MOBILE_TITLE_HANDLES.support,
              },
              {
                path: '/admin/data',
                element: <AdminDataPage />,
                handle: MOBILE_TITLE_HANDLES.adminData,
              },
              {
                path: '/admin/stats',
                element: <AdminStatsPage />,
                handle: MOBILE_TITLE_HANDLES.adminStats,
              },
              {
                path: '/admin/compliance',
                element: <AdminCompliancePage />,
                handle: MOBILE_TITLE_HANDLES.adminCompliance,
              },
              {
                path: '/admin/compliance/instruments/new',
                element: <AdminComplianceNewInstrumentPage />,
                handle: MOBILE_TITLE_HANDLES.adminCompliance,
              },
              {
                path: '/admin/compliance/instruments/:instrumentId',
                element: <AdminComplianceInstrumentPage />,
                handle: MOBILE_TITLE_HANDLES.adminCompliance,
              },
              {
                path: '/admin/compliance/instruments/:instrumentId/rules/:itemId',
                element: <AdminComplianceRuleViewPage />,
                handle: MOBILE_TITLE_HANDLES.adminCompliance,
              },
              {
                path: '/admin/compliance/instruments/:instrumentId/rules/:itemId/windows/:windowId',
                element: <AdminComplianceRuleViewPage />,
                handle: MOBILE_TITLE_HANDLES.adminCompliance,
              },
              {
                path: '/admin/compliance/instruments/:instrumentId/rules/:itemId/windows/:windowId/edit',
                element: <AdminComplianceRuleEditPage />,
                handle: MOBILE_TITLE_HANDLES.adminCompliance,
              },
              ...(import.meta.env.DEV ? devRoutes() : []),
              ...(import.meta.env.DEV ||
              import.meta.env.VITE_APP_ENV === 'sandbox'
                ? devDocsRoutes()
                : []),
              {
                path: '*',
                element: <NotFoundPage variant="in-layout" />,
                handle: MOBILE_TITLE_HANDLES.notFound,
              },
            ],
          },
        ],
      },
      // Root redirects to dashboard (ProtectedRoute will handle auth redirects)
      { path: '/', element: <Navigate to="/dashboard" replace /> },
    ],
  },
]);
